import type * as lazyIndex from './lazyIndex';
import {
    createLazyComponent,
    LazyModule,
    registerLazyOrchestrator,
    LazyAction,
    type LazyComponent,
} from 'owa-bundling';
import renderMeetingPreviewPlaceHolder from './components/MailListItemMeetingPreviewPlaceholder';
import { onModuleClick } from 'owa-workloads';
import { BigHoverActionPlaceholder } from 'owa-mail-list-view-bighoveraction';
import type { BigHoverActionProps } from './components/BigHoverAction/BigHoverAction';
import type { SelectAllCheckboxProps } from './components/SelectAllCheckbox';
import type { MailPersonaSearchResultsHeaderProps } from './components/listHeaders/MailPersonaSearchResultsHeader';
import type { CopilotResultsTabProps } from './components/listHeaders/CopilotResultsTab';
import type { MailListItemMeetingPreviewProps } from './components/MailListItemMeetingPreview';
import type { MailListItemExpansionProps } from './components/MailListItemExpansion';
import type { InboxBriefingsNudgeProps } from './components/nudges/InboxBriefingsNudge';
import type { ReplyNudgeProps } from './components/nudges/ReplyNudge';
import type { NeedsReplyBarProps } from './components/nudges/NeedsReplyBar';
import type { NeedsReplyViewHeaderButtonsProps } from './components/nudges/NeedsReplyViewHeaderButtons';
import type { PersonalOSNudgeProps } from './components/nudges/PersonalOSNudge';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(
    () => import(/* webpackChunkName: "MailListItemLazy"*/ './lazyIndex'),
    { name: 'MailListItemLazy' }
);

// Delayed Loaded Components
export const MailListItemExpansion: LazyComponent<MailListItemExpansionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.MailListItemExpansion);
export const MailListItemMeetingPreview: LazyComponent<
    MailListItemMeetingPreviewProps,
    unknown,
    void
> = createLazyComponent(
    lazyModule,
    m => m.MailListItemMeetingPreview,
    () => renderMeetingPreviewPlaceHolder()
);
export const MailPersonaSearchResultsHeader: LazyComponent<
    MailPersonaSearchResultsHeaderProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.MailPersonaSearchResultsHeader);

export const CopilotResultsTab: LazyComponent<CopilotResultsTabProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CopilotResultsTab);

export const MailListItemNoMoreRowsMessageBar: typeof lazyIndex.MailListItemNoMoreRowsMessageBar =
    createLazyComponent(lazyModule, m => m.MailListItemNoMoreRowsMessageBar);

registerLazyOrchestrator(onModuleClick, lazyModule, m => m.onModuleClickOrchestrator);

export const SelectAllCheckbox: LazyComponent<SelectAllCheckboxProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.SelectAllCheckbox);

export const BigHoverAction: LazyComponent<BigHoverActionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.BigHoverAction, BigHoverActionPlaceholder);

export const lazySetupTriageActionKeys: LazyAction<
    typeof lazyIndex.setupTriageActionKeys,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setupTriageActionKeys);

export const InboxBriefingsNudge: LazyComponent<InboxBriefingsNudgeProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.InboxBriefingsNudge);

export const ReplyNudge: LazyComponent<ReplyNudgeProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.ReplyNudge
);

export const NeedsReplyBar: LazyComponent<NeedsReplyBarProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.NeedsReplyBar
);

export const NeedsReplyViewHeaderButtons: LazyComponent<
    NeedsReplyViewHeaderButtonsProps,
    unknown,
    void
> = createLazyComponent(lazyModule, m => m.NeedsReplyViewHeaderButtons);

export const lazyOpenNeedsReplyView: LazyAction<
    typeof lazyIndex.openNeedsReplyView,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openNeedsReplyView);

export const PersonalOSNudge: LazyComponent<PersonalOSNudgeProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.PersonalOSNudge);
